import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useMemo, useState } from 'react';
import { formatDay } from 'pages/data-import/utils';
import { AccountCell, Muted, Sub, TiktokIdCell, UidCell } from '../components/cells';
import IssueTable, { IssueField } from '../components/issue-table';
import { RenameRow } from '../types';
import { useIssueList } from '../use-issue-list';
import {
  formatCount,
  latestEvent,
  renameChangeText,
  renameWhenText,
  sortEvents,
  toCount,
} from '../utils';
import styles from '../style.module.scss';

const rowKey = (row: RenameRow) => String(row.tiktokUid);

/** Số lần đổi: theo máy chủ; thiếu thì đếm từ danh sách các lần đổi. */
const changesOf = (row: RenameRow) =>
  toCount(row.changeCount) || (Array.isArray(row.events) ? row.events.length : 0);

/** Dòng mở rộng: từng lần đổi, từ cũ tới mới. */
const RenameHistory = ({ row }: { row: RenameRow }) => {
  const events = sortEvents(row.events);
  return (
    <div className={styles.history}>
      {row.firstSeen && (
        <div className={styles.sub}>Có trong file lần đầu: {formatDay(row.firstSeen)}</div>
      )}
      {events.length ? (
        <ol className={styles.events}>
          {events.map((event, index) => (
            <li key={`${event.from}-${event.to}-${event.firstSeenNew}-${index}`}>
              <span className={styles.wrapAnywhere}>{renameChangeText(event)}</span>,{' '}
              {renameWhenText(event)}
            </li>
          ))}
        </ol>
      ) : (
        <Muted>Chưa có chi tiết từng lần đổi.</Muted>
      )}
    </div>
  );
};

/**
 * Tab "Đổi TikTok ID": mỗi UID đã đổi TikTok ID ít nhất một lần. Lịch sử suy ra từ các file đã
 * nhập (mỗi dòng số liệu lưu UID + TikTok ID lúc xuất file). Bấm "Xem từng lần đổi" để mở dòng.
 */
const RenamesTab = () => {
  const list = useIssueList<RenameRow>('renames', 'Không tải được danh sách Đổi TikTok ID');
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const fields = useMemo<IssueField<RenameRow>[]>(() => {
    const toggle = (key: string) =>
      setOpenKeys((keys) =>
        keys.includes(key) ? keys.filter((item) => item !== key) : [...keys, key]
      );
    // Tổng 1.055px, vừa khung nội dung của màn 1440. Cột Số lần đổi đủ rộng để số và nút nằm
    // trên một dòng; cột cuối đủ rộng cho "trong khoảng DD/MM/YYYY → DD/MM/YYYY".
    return [
      {
        key: 'tiktokUid',
        title: 'UID',
        width: 175,
        render: (row) => <UidCell uid={row.tiktokUid} />,
      },
      {
        key: 'currentUsername',
        title: 'TikTok ID hiện tại',
        width: 175,
        render: (row) => <TiktokIdCell username={row.currentUsername} />,
      },
      {
        key: 'user',
        title: 'Tên trong app',
        width: 190,
        render: (row) => <AccountCell user={row.user} username={row.appUsername} />,
      },
      {
        key: 'changeCount',
        title: 'Số lần đổi',
        width: 215,
        render: (row) => {
          const key = rowKey(row);
          const open = openKeys.includes(key);
          return (
            <div className={styles.changes}>
              <span>{formatCount(changesOf(row))} lần</span>
              <Button
                size="small"
                icon={open ? <UpOutlined /> : <DownOutlined />}
                aria-expanded={open}
                onClick={() => toggle(key)}
              >
                {open ? 'Thu gọn' : 'Xem từng lần đổi'}
              </Button>
            </div>
          );
        },
      },
      {
        key: 'latest',
        title: 'Lần đổi gần nhất',
        width: 300,
        render: (row) => {
          const latest = latestEvent(row.events);
          if (!latest) return '—';
          return (
            <div className={styles.wrapAnywhere}>
              {renameWhenText(latest)}
              <Sub>{renameChangeText(latest)}</Sub>
            </div>
          );
        },
      },
    ];
  }, [openKeys]);

  return (
    <IssueTable
      list={list}
      fields={fields}
      rowKey={rowKey}
      searchPlaceholder="Tìm TikTok ID, UID hoặc tên"
      searchLabel="Tìm theo TikTok ID, UID hoặc tên trong app"
      // Điện thoại: dòng đầu của mỗi ô là TikTok ID hiện tại (dễ nhận ra hơn dãy số UID).
      compactHead={{ key: 'currentUsername', showTitle: true }}
      expanded={{ keys: openKeys, render: (row) => <RenameHistory row={row} /> }}
      note={
        <>
          Creator đã đổi TikTok ID ít nhất một lần. UID không đổi nên số liệu vẫn liền mạch. Biết
          đúng ngày đổi khi có file của hai ngày liền nhau; nếu không thì chỉ biết khoảng ngày.
        </>
      }
    />
  );
};

export default RenamesTab;
